import { OnlineStatusContext } from '@/contexts/onlineStatus';
import { useCurrentUser } from '@/hooks/dataHelpers';
import { useRealtimeDbBatchMutation, useRealtimeDbProviderName } from '@/hooks/realtimeDataHelpers';
import { saveItemToOfflineDB } from '@/lib/offlineDatabase/OfflineDatabase';
import {
  selectLogs,
  selectSkipRealtimeLogs,
  useRealtimeExperienceStore,
} from '@/pages/experiences/store/realtimeExperienceStore';
import { buildRealtimeLogPayload } from '@/pages/experiences/utils/realtimeExperienceHelper';
import { selectOriginalUserRole, useAuthStore } from '@/store';
import { selectExperience, useExperienceStore } from '@/store/experienceStore';
import * as Sentry from '@sentry/react';
import { useContext } from 'react';

export const useRealtimeLogs = () => {
  const logs = useRealtimeExperienceStore(selectLogs);
  const currentUser = useCurrentUser();
  const experience = useExperienceStore(selectExperience);
  const { batchMutation, loading } = useRealtimeDbBatchMutation();
  const { isOnline, userIp } = useContext(OnlineStatusContext);
  const originalUserRole = useAuthStore(selectOriginalUserRole);
  const shouldSkipRealtimeLogs = useRealtimeExperienceStore(selectSkipRealtimeLogs);
  const isSupabaseProvider = useRealtimeDbProviderName() === 'supabase';
  /**
   * Sets realtime logs with appropriate collection routing
   *
   * Collection routing logic:
   * - 'experiences' collection: Used for student and monitor logs (default)
   * - 'orgs' collection: Used for experience actions and CRUD operations
   *
   * Preconditions:
   * - currentUser.id must be present
   * - experience.id must be present
   * - experience.org_id must be present
   *
   * Note: Logging is automatically skipped for users with 'superadmin' or 'support' roles or if skipRealtimeLogs is true.
   * This function will return early (no-op) for these privileged roles.
   *
   * @param log - The log data to be stored
   * @param collection - Target collection ('experiences' | 'orgs')
   *                    Defaults to 'experiences' for student/monitor logs
   *                    Use 'orgs' for experience actions and CRUD operations
   */
  const setLogs = async (log: any, collection: 'experiences' | 'orgs' = 'experiences') => {
    // Skip logging for superadmin and support roles or if skipRealtimeLogs is true
    if (
      originalUserRole === 'superadmin' ||
      originalUserRole === 'support' ||
      shouldSkipRealtimeLogs
    ) {
      return;
    }

    const realtimeLogPayload = buildRealtimeLogPayload(log, {
      currentUser,
      experience,
      userIp,
      isOnline,
      collection,
    });
    // Supabase has no offline write cache, so an offline row died with the link
    // (APL-12454). Firestore replays its own; log_id keeps the resend idempotent.
    if (currentUser?.role === 'student' && isSupabaseProvider) {
      try {
        saveItemToOfflineDB(
          'logs',
          { log_id: realtimeLogPayload?.data?.log_id },
          realtimeLogPayload.data
        );
      } catch (error) {
        Sentry.captureException(
          new Error(`Failed to save log to offline database in useRealtimeLogs: ${error}`)
        );
      }
    }
    batchMutation({
      values: [realtimeLogPayload],
      meta: {},
    });
  };

  return {
    logs,
    loading,
    setLogs,
  };
};
